import { z } from "zod";

/**
 * Access levels, ordered weakest to strongest. The array order *is* the
 * comparison order — `rank()` below depends on it, so only append.
 */
export const ACCESS_LEVELS = ["none", "viewer", "editor", "owner"] as const;
export const accessLevelSchema = z.enum(ACCESS_LEVELS);
export type AccessLevel = z.infer<typeof accessLevelSchema>;

/** Roles a member can hold in a team. `admin` manages members; `owner` also deletes the team. */
export const TEAM_ROLES = ["viewer", "editor", "admin", "owner"] as const;
export const teamRoleSchema = z.enum(TEAM_ROLES);
export type TeamRole = z.infer<typeof teamRoleSchema>;

const rank = (level: AccessLevel) => ACCESS_LEVELS.indexOf(level);

/** True when `have` is at least as strong as `need`. */
export function satisfies(have: AccessLevel, need: AccessLevel): boolean {
  return rank(have) >= rank(need);
}

/** The strongest of the given levels — used to merge team role with an explicit share. */
export function strongest(...levels: AccessLevel[]): AccessLevel {
  return levels.reduce<AccessLevel>((best, l) => (rank(l) > rank(best) ? l : best), "none");
}

/** A team role grants this much access to the team's resources. */
export function accessFromTeamRole(role: TeamRole): AccessLevel {
  switch (role) {
    case "owner":
      return "owner";
    case "admin":
    case "editor":
      return "editor";
    case "viewer":
      return "viewer";
  }
}
